/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type { LearnerProfile_EducationHistory as EducationHistory } from '__generated__/graphql-types-do-not-use';

import { Divider, InlineNotification, ShowMoreList } from '@coursera/cds-core';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import CredentialsSectionBadgeItem from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionBadgeItem';
import {
  CredentialEmptyState,
  EducationEmptyState,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionEmptyState';
import {
  CredentialsSectionCredentialItem,
  CredentialsSectionEducationItem,
  CredentialsSectionItemPlaceholder,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionItem';
import { credentialType } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/credentialUtils';
import { onlyCourseraIssuedBadges } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileUtils';

import _t from 'i18n!nls/account-profile';

type CredentialsSectionBodyProps = {
  openEducationDialog: (education?: EducationHistory) => void;
};

const MAX_ITEMS_TO_SHOW = 3;

const styles = {
  wrapper: () => css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  divider: () => css`
    margin: var(--cds-spacing-200) 0;
  `,
  courseraQualificationErrorNotification: css`
    margin-top: var(--cds-spacing-200);
  `,
};

const CredentialsSectionBody = ({ openEducationDialog }: CredentialsSectionBodyProps) => {
  const { profileState = {}, isProfileLoading, editable, courseraHistoryFailedToLoad } = useContext(ProfileContext);

  // Loading state
  if (isProfileLoading) {
    return (
      <div role="status">
        <CredentialsSectionItemPlaceholder />
        <Divider color="light" css={styles.divider} />
        <CredentialsSectionItemPlaceholder />
        <Divider color="light" css={styles.divider} />
        <CredentialsSectionItemPlaceholder />
      </div>
    );
  }

  const {
    educationHistory = [],
    badges,
    courseraHistory = {
      completedProfessionalCertificates: [],
      completedSpecializations: [],
    },
  } = profileState;
  const courseraIssuedBadges = onlyCourseraIssuedBadges(badges?.badges ?? []);

  const certificates = courseraHistory?.completedProfessionalCertificates;
  const specializations = courseraHistory?.completedSpecializations;

  const educationHistoryLength = educationHistory.length;
  const certificatesLength = certificates?.length || 0;
  const specializationsLength = specializations?.length || 0;
  const badgesLength = courseraIssuedBadges.length;
  const totalItemsLength = educationHistoryLength + badgesLength + certificatesLength + specializationsLength;

  // I make a copy of the array to avoid sorting in place which sometimes throws an error
  const sortedEducationHistory = [...educationHistory]?.sort((e1, e2) => {
    if (e1.isCurrentlyAStudent) {
      return -1;
    } else if (e2.isCurrentlyAStudent) {
      return 1;
    } else if (e1.graduationDate && e2.graduationDate) {
      return new Date(e1.graduationDate.year, e1.graduationDate.month) <
        new Date(e2.graduationDate.year, e2.graduationDate.month)
        ? 1
        : -1;
    } else if (e1.graduationDate) {
      return -1;
    } else {
      return 1;
    }
  });

  const ItemWrapperComponent =
    totalItemsLength > MAX_ITEMS_TO_SHOW
      ? ({ children, ...props }: { children: React.ReactNode[] }) => (
          <ShowMoreList
            numItemsToShow={MAX_ITEMS_TO_SHOW}
            controllerProps={{ itemTypePlural: _t('credentials'), ariaLabel: _t('Show all credentials') }}
            {...props}
          >
            {children}
          </ShowMoreList>
        )
      : ({ children, ...props }: { children: React.ReactNode }) => <div {...props}>{children}</div>;

  return (
    <div css={styles.wrapper}>
      {editable && (
        <>
          {educationHistoryLength === 0 && <EducationEmptyState openEducationDialog={openEducationDialog} />}
          {certificatesLength + specializationsLength === 0 && <CredentialEmptyState />}
        </>
      )}
      {(totalItemsLength > 0 || courseraHistoryFailedToLoad) && (
        <ItemWrapperComponent>
          {sortedEducationHistory.map((education, idx) => (
            <div key={`${education.institutionName}_${education.degreeType}`}>
              <CredentialsSectionEducationItem education={education} openEducationDialog={openEducationDialog} />
              {idx < totalItemsLength - 1 && (
                <Divider color="light" css={styles.divider} data-testid="credential-item-divider" />
              )}
            </div>
          ))}
          {/**
           * We show an error notification if there was an error loading the Coursera certificate data.
           * As there is no way to distinguish between a user who has no Coursera certificates and a user
           * who has Coursera certificates but there was an error loading them
           */}
          {courseraHistoryFailedToLoad && (
            <InlineNotification
              severity="error"
              css={styles.courseraQualificationErrorNotification}
              title={_t("We're sorry, we can't load the Coursera certificate information data right now")}
            >
              {_t('There was an error loading the Coursera certificate data. Please try again later.')}
            </InlineNotification>
          )}
          {courseraIssuedBadges.map((badge) => (
            <div key={badge.badgeTemplate.badgeTemplateId}>
              <CredentialsSectionBadgeItem badge={badge} />
            </div>
          ))}
          {certificates?.map((cert, idx) => (
            <div key={cert.verifyCode}>
              <CredentialsSectionCredentialItem credential={cert} type={credentialType.CERTIFICATE} />
              {educationHistoryLength + idx < totalItemsLength - 1 && (
                <Divider color="light" css={styles.divider} data-testid="credential-item-divider" />
              )}
            </div>
          ))}
          {specializations?.map((spec, idx) => (
            <div key={spec.verifyCode}>
              <CredentialsSectionCredentialItem credential={spec} type={credentialType.SPECIALIZATION} />
              {educationHistoryLength + certificatesLength + idx < totalItemsLength - 1 && (
                <Divider color="light" css={styles.divider} data-testid="credential-item-divider" />
              )}
            </div>
          ))}
        </ItemWrapperComponent>
      )}
    </div>
  );
};

export default CredentialsSectionBody;
